// ============================================================================
// AI-GENERATED TRANSLATIONS — REVIEW BEFORE PRODUCTION USE
// ----------------------------------------------------------------------------
// The non-English translation tables in this file (Spanish, Chinese,
// Vietnamese, Arabic, Tagalog) were generated with AI assistance. They aim for
// natural, context-appropriate UI wording, but they have NOT been verified by
// native speakers. Before relying on these strings in production, have a
// fluent/native speaker of each language review them for accuracy, tone, and
// cultural appropriateness (especially Arabic, which is rendered right-to-left).
// ============================================================================

export interface TranslationStrings {
  [key: string]: string;
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  direction: 'ltr' | 'rtl';
  flag: string;
}

// P4 fix (2026-05-17): Restricted to only the languages with actual translation
// files (English fully, Spanish partial). Previously declared 10 languages but 8
// had no translations, causing missing-key noise. Add more by:
//   1. Adding the translation table below (e.g., FRENCH_TRANSLATIONS).
//   2. Wiring it into getTranslation().
//   3. Adding the entry to SUPPORTED_LANGUAGES.
// The fallbackLng pattern (English) is implemented in getTranslation() so any
// missing key gracefully degrades to English rather than rendering the raw key.
export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr', flag: '🇪🇸' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', direction: 'ltr', flag: '🇨🇳' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', direction: 'ltr', flag: '🇻🇳' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl', flag: '🇸🇦' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', direction: 'ltr', flag: '🇵🇭' },
];

export const DEFAULT_LANGUAGE = 'en';

/**
 * Languages we have translation data for. Distinct from the marketing list:
 * `SUPPORTED_LANGUAGES` is what shows up in the picker UI.
 */
export const AVAILABLE_TRANSLATION_LANGUAGES = ['en', 'es', 'zh', 'vi', 'ar', 'tl'] as const;

export const ENGLISH_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': 'Home',
  'nav.dashboard': 'Dashboard',
  'nav.games': 'My Games',
  'nav.projects': 'Projects',
  'nav.classroom': 'Classroom',
  'nav.help': 'Help',
  'nav.settings': 'Settings',
  'nav.profile': 'Profile',
  'nav.logout': 'Logout',
  'nav.signIn': 'Sign In',
  'nav.signUp': 'Sign Up',

  // Buttons
  'btn.newGame': 'New Game',
  'btn.createProject': 'Create Project',
  'btn.save': 'Save',
  'btn.cancel': 'Cancel',
  'btn.delete': 'Delete',
  'btn.edit': 'Edit',
  'btn.share': 'Share',
  'btn.play': 'Play',
  'btn.test': 'Test',
  'btn.submit': 'Submit',
  'btn.next': 'Next',
  'btn.previous': 'Previous',
  'btn.finish': 'Finish',
  'btn.learn': 'Learn More',

  // Labels
  'label.title': 'Title',
  'label.description': 'Description',
  'label.author': 'Author',
  'label.date': 'Date',
  'label.status': 'Status',
  'label.tags': 'Tags',
  'label.difficulty': 'Difficulty',
  'label.language': 'Language',
  'label.theme': 'Theme',

  // Forms
  'form.email': 'Email Address',
  'form.password': 'Password',
  'form.confirmPassword': 'Confirm Password',
  'form.username': 'Username',
  'form.firstName': 'First Name',
  'form.lastName': 'Last Name',
  'form.schoolName': 'School Name',
  'form.gradeLevel': 'Grade Level',
  'form.terms': 'I agree to the Terms of Service',
  'form.privacy': 'I accept the Privacy Policy',

  // Messages
  'msg.welcome': 'Welcome',
  'msg.loading': 'Loading...',
  'msg.error': 'Error',
  'msg.success': 'Success!',
  'msg.saved': 'Saved successfully',
  'msg.deleted': 'Deleted successfully',
  'msg.noResults': 'No results found',
  'msg.tryAgain': 'Please try again',
  'msg.confirmDelete': 'Are you sure you want to delete this?',

  // Dashboard
  'dashboard.title': 'My Dashboard',
  'dashboard.recentGames': 'Recent Games',
  'dashboard.projects': 'Projects',
  'dashboard.achievements': 'Achievements',
  'dashboard.progress': 'Progress',
  'dashboard.gamesCreated': 'Games Created',
  'dashboard.hoursSpent': 'Hours Spent',
  'dashboard.friendsAdded': 'Friends Added',

  // Games
  'game.newGame': 'New Game',
  'game.editGame': 'Edit Game',
  'game.deleteGame': 'Delete Game',
  'game.shareGame': 'Share Game',
  'game.playGame': 'Play Game',
  'game.codeEditor': 'Code Editor',
  'game.preview': 'Preview',
  'game.assets': 'Assets',
  'game.sounds': 'Sounds',
  'game.sprites': 'Sprites',

  // Code Editor
  'editor.undo': 'Undo',
  'editor.redo': 'Redo',
  'editor.copy': 'Copy',
  'editor.paste': 'Paste',
  'editor.delete': 'Delete',
  'editor.format': 'Format Code',
  'editor.console': 'Console',
  'editor.errors': 'Errors',
  'editor.warnings': 'Warnings',

  // Classroom
  'classroom.createClass': 'Create Classroom',
  'classroom.students': 'Students',
  'classroom.assignments': 'Assignments',
  'classroom.grades': 'Grades',
  'classroom.roster': 'Class Roster',
  'classroom.settings': 'Classroom Settings',
  'classroom.addStudent': 'Add Student',
  'classroom.removeStudent': 'Remove Student',

  // Help
  'help.searchHelp': 'Search help articles',
  'help.gettingStarted': 'Getting Started',
  'help.codeHelp': 'Code Help',
  'help.gameBuilding': 'Game Building',
  'help.account': 'Account',
  'help.troubleshooting': 'Troubleshooting',
  'help.faq': 'Frequently Asked Questions',
  'help.contact': 'Contact Support',

  // Settings
  'settings.profile': 'Profile',
  'settings.account': 'Account',
  'settings.notifications': 'Notifications',
  'settings.privacy': 'Privacy',
  'settings.accessibility': 'Accessibility',
  'settings.appearance': 'Appearance',
  'settings.language': 'Language',
  'settings.theme': 'Theme',

  // Accessibility
  'a11y.darkMode': 'Dark Mode',
  'a11y.fontSize': 'Font Size',
  'a11y.highContrast': 'High Contrast',
  'a11y.screenReader': 'Screen Reader Support',

  // Errors
  'error.notFound': 'Page not found',
  'error.unauthorized': 'You are not authorized to access this',
  'error.forbidden': 'Access forbidden',
  'error.serverError': 'Server error occurred',
  'error.timeout': 'Request timed out',
  'error.invalidEmail': 'Invalid email address',
  'error.passwordTooShort': 'Password must be at least 8 characters',
  'error.passwordMismatch': 'Passwords do not match',

  // Onboarding
  'onboarding.welcome': 'Welcome to NS Gaming Studio',
  'onboarding.step1': 'Getting Started',
  'onboarding.step2': 'Your First Game',
  'onboarding.step3': 'Share Your Game',
  'onboarding.complete': 'Setup Complete',
  'onboarding.skipTutorial': 'Skip Tutorial',

  // AI Assistant
  'ai.gameBuddy': 'Game Buddy',
  'ai.helpWithCode': 'Help with Code',
  'ai.suggestFeature': 'Suggest Feature',
  'ai.findBug': 'Find Bug',
  'ai.explain': 'Explain This',
  'ai.improveCode': 'Improve Code',

  // Social
  'social.share': 'Share',
  'social.like': 'Like',
  'social.comment': 'Comment',
  'social.follow': 'Follow',
  'social.unfollow': 'Unfollow',
  'social.friends': 'Friends',
  'social.followers': 'Followers',

  // Notifications
  'notif.newMessage': 'New Message',
  'notif.friendRequest': 'Friend Request',
  'notif.classroomUpdate': 'Classroom Update',
  'notif.assignmentDue': 'Assignment Due',
  'notif.gradeReceived': 'Grade Received',
  'notif.achievementUnlocked': 'Achievement Unlocked',

  // Time
  'time.today': 'Today',
  'time.yesterday': 'Yesterday',
  'time.thisWeek': 'This Week',
  'time.thisMonth': 'This Month',
  'time.justNow': 'Just Now',

  // Lessons
  'lesson.createLesson': 'Create Lesson',
  'lesson.editLesson': 'Edit Lesson',
  'lesson.deleteLesson': 'Delete Lesson',
  'lesson.objectives': 'Learning Objectives',
  'lesson.materials': 'Materials',
  'lesson.activities': 'Activities',
  'lesson.assessment': 'Assessment',

  // Templates
  'template.selectTemplate': 'Select a Template',
  'template.blankProject': 'Blank Project',
  'template.platformer': 'Platformer Game',
  'template.puzzle': 'Puzzle Game',
  'template.runnerGame': 'Runner Game',
  'template.shooterGame': 'Shooter Game',

  // Achievements
  'achievement.firstGame': 'First Game',
  'achievement.tenGames': 'Game Creator',
  'achievement.hundredLines': 'Code Master',
  'achievement.shareGame': 'Share Master',
  'achievement.helper': 'Helper',
};

export const SPANISH_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': 'Inicio',
  'nav.dashboard': 'Panel de Control',
  'nav.games': 'Mis Juegos',
  'nav.projects': 'Proyectos',
  'nav.classroom': 'Clase',
  'nav.help': 'Ayuda',
  'nav.settings': 'Configuración',
  'nav.profile': 'Perfil',
  'nav.logout': 'Cerrar sesión',
  'nav.signIn': 'Iniciar sesión',
  'nav.signUp': 'Registrarse',

  // Buttons
  'btn.newGame': 'Nuevo Juego',
  'btn.createProject': 'Crear Proyecto',
  'btn.save': 'Guardar',
  'btn.cancel': 'Cancelar',
  'btn.delete': 'Eliminar',
  'btn.edit': 'Editar',
  'btn.share': 'Compartir',
  'btn.play': 'Jugar',
  'btn.test': 'Probar',
  'btn.submit': 'Enviar',
  'btn.next': 'Siguiente',
  'btn.previous': 'Anterior',
  'btn.finish': 'Finalizar',
  'btn.learn': 'Más Información',

  // Labels
  'label.title': 'Título',
  'label.description': 'Descripción',
  'label.author': 'Autor',
  'label.date': 'Fecha',
  'label.status': 'Estado',
  'label.tags': 'Etiquetas',
  'label.difficulty': 'Dificultad',
  'label.language': 'Idioma',
  'label.theme': 'Tema',

  // Forms
  'form.email': 'Correo Electrónico',
  'form.password': 'Contraseña',
  'form.confirmPassword': 'Confirmar Contraseña',
  'form.username': 'Nombre de Usuario',
  'form.firstName': 'Nombre',
  'form.lastName': 'Apellido',
  'form.schoolName': 'Nombre de la Escuela',
  'form.gradeLevel': 'Grado Escolar',
  'form.terms': 'Acepto los Términos del Servicio',
  'form.privacy': 'Acepto la Política de Privacidad',

  // Messages
  'msg.welcome': 'Bienvenido',
  'msg.loading': 'Cargando...',
  'msg.error': 'Error',
  'msg.success': '¡Éxito!',
  'msg.saved': 'Guardado exitosamente',
  'msg.deleted': 'Eliminado exitosamente',
  'msg.noResults': 'No se encontraron resultados',
  'msg.tryAgain': 'Por favor, inténtalo de nuevo',
  'msg.confirmDelete': '¿Estás seguro de que quieres eliminar esto?',

  // Dashboard
  'dashboard.title': 'Mi Panel',
  'dashboard.recentGames': 'Juegos Recientes',
  'dashboard.projects': 'Proyectos',
  'dashboard.achievements': 'Logros',
  'dashboard.progress': 'Progreso',
  'dashboard.gamesCreated': 'Juegos Creados',
  'dashboard.hoursSpent': 'Horas Dedicadas',
  'dashboard.friendsAdded': 'Amigos Agregados',

  // Games
  'game.newGame': 'Nuevo Juego',
  'game.editGame': 'Editar Juego',
  'game.deleteGame': 'Eliminar Juego',
  'game.shareGame': 'Compartir Juego',
  'game.playGame': 'Jugar',
  'game.codeEditor': 'Editor de Código',
  'game.preview': 'Vista Previa',
  'game.assets': 'Recursos',
  'game.sounds': 'Sonidos',
  'game.sprites': 'Sprites',

  // Code Editor
  'editor.undo': 'Deshacer',
  'editor.redo': 'Rehacer',
  'editor.copy': 'Copiar',
  'editor.paste': 'Pegar',
  'editor.delete': 'Eliminar',
  'editor.format': 'Formatear Código',
  'editor.console': 'Consola',
  'editor.errors': 'Errores',
  'editor.warnings': 'Advertencias',

  // Classroom
  'classroom.createClass': 'Crear Clase',
  'classroom.students': 'Estudiantes',
  'classroom.assignments': 'Tareas',
  'classroom.grades': 'Calificaciones',
  'classroom.roster': 'Lista de la Clase',
  'classroom.settings': 'Configuración de la Clase',
  'classroom.addStudent': 'Agregar Estudiante',
  'classroom.removeStudent': 'Quitar Estudiante',

  // Help
  'help.searchHelp': 'Buscar artículos de ayuda',
  'help.gettingStarted': 'Primeros Pasos',
  'help.codeHelp': 'Ayuda de Código',
  'help.gameBuilding': 'Creación de Juegos',
  'help.account': 'Cuenta',
  'help.troubleshooting': 'Solución de Problemas',
  'help.faq': 'Preguntas Frecuentes',
  'help.contact': 'Contactar con Soporte',

  // Settings
  'settings.profile': 'Perfil',
  'settings.account': 'Cuenta',
  'settings.notifications': 'Notificaciones',
  'settings.privacy': 'Privacidad',
  'settings.accessibility': 'Accesibilidad',
  'settings.appearance': 'Apariencia',
  'settings.language': 'Idioma',
  'settings.theme': 'Tema',

  // Accessibility
  'a11y.darkMode': 'Modo Oscuro',
  'a11y.fontSize': 'Tamaño de Fuente',
  'a11y.highContrast': 'Alto Contraste',
  'a11y.screenReader': 'Compatibilidad con Lector de Pantalla',

  // Errors
  'error.notFound': 'Página no encontrada',
  'error.unauthorized': 'No tienes autorización para acceder a esto',
  'error.forbidden': 'Acceso denegado',
  'error.serverError': 'Se produjo un error del servidor',
  'error.timeout': 'Se agotó el tiempo de espera de la solicitud',
  'error.invalidEmail': 'Correo electrónico no válido',
  'error.passwordTooShort': 'La contraseña debe tener al menos 8 caracteres',
  'error.passwordMismatch': 'Las contraseñas no coinciden',

  // Onboarding
  'onboarding.welcome': 'Bienvenido a NS Gaming Studio',
  'onboarding.step1': 'Primeros Pasos',
  'onboarding.step2': 'Tu Primer Juego',
  'onboarding.step3': 'Comparte Tu Juego',
  'onboarding.complete': 'Configuración Completada',
  'onboarding.skipTutorial': 'Omitir Tutorial',

  // AI Assistant
  'ai.gameBuddy': 'Compañero de Juego',
  'ai.helpWithCode': 'Ayuda con el Código',
  'ai.suggestFeature': 'Sugerir Función',
  'ai.findBug': 'Encontrar Error',
  'ai.explain': 'Explicar Esto',
  'ai.improveCode': 'Mejorar el Código',

  // Social
  'social.share': 'Compartir',
  'social.like': 'Me gusta',
  'social.comment': 'Comentar',
  'social.follow': 'Seguir',
  'social.unfollow': 'Dejar de seguir',
  'social.friends': 'Amigos',
  'social.followers': 'Seguidores',

  // Notifications
  'notif.newMessage': 'Nuevo Mensaje',
  'notif.friendRequest': 'Solicitud de Amistad',
  'notif.classroomUpdate': 'Actualización de la Clase',
  'notif.assignmentDue': 'Tarea por Vencer',
  'notif.gradeReceived': 'Calificación Recibida',
  'notif.achievementUnlocked': 'Logro Desbloqueado',

  // Time
  'time.today': 'Hoy',
  'time.yesterday': 'Ayer',
  'time.thisWeek': 'Esta Semana',
  'time.thisMonth': 'Este Mes',
  'time.justNow': 'Ahora Mismo',

  // Lessons
  'lesson.createLesson': 'Crear Lección',
  'lesson.editLesson': 'Editar Lección',
  'lesson.deleteLesson': 'Eliminar Lección',
  'lesson.objectives': 'Objetivos de Aprendizaje',
  'lesson.materials': 'Materiales',
  'lesson.activities': 'Actividades',
  'lesson.assessment': 'Evaluación',

  // Templates
  'template.selectTemplate': 'Selecciona una Plantilla',
  'template.blankProject': 'Proyecto en Blanco',
  'template.platformer': 'Juego de Plataformas',
  'template.puzzle': 'Juego de Rompecabezas',
  'template.runnerGame': 'Juego de Carreras',
  'template.shooterGame': 'Juego de Disparos',

  // Achievements
  'achievement.firstGame': 'Primer Juego',
  'achievement.tenGames': 'Creador de Juegos',
  'achievement.hundredLines': 'Maestro del Código',
  'achievement.shareGame': 'Maestro de Compartir',
  'achievement.helper': 'Ayudante',
};

export const CHINESE_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': '首页',
  'nav.dashboard': '仪表板',
  'nav.games': '我的游戏',
  'nav.projects': '项目',
  'nav.classroom': '课堂',
  'nav.help': '帮助',
  'nav.settings': '设置',
  'nav.profile': '个人资料',
  'nav.logout': '退出登录',
  'nav.signIn': '登录',
  'nav.signUp': '注册',

  // Buttons
  'btn.newGame': '新建游戏',
  'btn.createProject': '创建项目',
  'btn.save': '保存',
  'btn.cancel': '取消',
  'btn.delete': '删除',
  'btn.edit': '编辑',
  'btn.share': '分享',
  'btn.play': '开始游戏',
  'btn.test': '测试',
  'btn.submit': '提交',
  'btn.next': '下一步',
  'btn.previous': '上一步',
  'btn.finish': '完成',
  'btn.learn': '了解更多',

  // Labels
  'label.title': '标题',
  'label.description': '描述',
  'label.author': '作者',
  'label.date': '日期',
  'label.status': '状态',
  'label.tags': '标签',
  'label.difficulty': '难度',
  'label.language': '语言',
  'label.theme': '主题',

  // Forms
  'form.email': '电子邮箱',
  'form.password': '密码',
  'form.confirmPassword': '确认密码',
  'form.username': '用户名',
  'form.firstName': '名字',
  'form.lastName': '姓氏',
  'form.schoolName': '学校名称',
  'form.gradeLevel': '年级',
  'form.terms': '我同意服务条款',
  'form.privacy': '我接受隐私政策',

  // Messages
  'msg.welcome': '欢迎',
  'msg.loading': '加载中…',
  'msg.error': '错误',
  'msg.success': '成功！',
  'msg.saved': '保存成功',
  'msg.deleted': '删除成功',
  'msg.noResults': '未找到结果',
  'msg.tryAgain': '请重试',
  'msg.confirmDelete': '确定要删除吗？',

  // Dashboard
  'dashboard.title': '我的仪表板',
  'dashboard.recentGames': '最近的游戏',
  'dashboard.projects': '项目',
  'dashboard.achievements': '成就',
  'dashboard.progress': '进度',
  'dashboard.gamesCreated': '已创建游戏',
  'dashboard.hoursSpent': '花费时长',
  'dashboard.friendsAdded': '已添加好友',

  // Games
  'game.newGame': '新建游戏',
  'game.editGame': '编辑游戏',
  'game.deleteGame': '删除游戏',
  'game.shareGame': '分享游戏',
  'game.playGame': '开始游戏',
  'game.codeEditor': '代码编辑器',
  'game.preview': '预览',
  'game.assets': '素材',
  'game.sounds': '音效',
  'game.sprites': '精灵图',

  // Code Editor
  'editor.undo': '撤销',
  'editor.redo': '重做',
  'editor.copy': '复制',
  'editor.paste': '粘贴',
  'editor.delete': '删除',
  'editor.format': '格式化代码',
  'editor.console': '控制台',
  'editor.errors': '错误',
  'editor.warnings': '警告',

  // Classroom
  'classroom.createClass': '创建课堂',
  'classroom.students': '学生',
  'classroom.assignments': '作业',
  'classroom.grades': '成绩',
  'classroom.roster': '班级名单',
  'classroom.settings': '课堂设置',
  'classroom.addStudent': '添加学生',
  'classroom.removeStudent': '移除学生',

  // Help
  'help.searchHelp': '搜索帮助文章',
  'help.gettingStarted': '入门指南',
  'help.codeHelp': '代码帮助',
  'help.gameBuilding': '游戏制作',
  'help.account': '账户',
  'help.troubleshooting': '故障排除',
  'help.faq': '常见问题',
  'help.contact': '联系支持',

  // Settings
  'settings.profile': '个人资料',
  'settings.account': '账户',
  'settings.notifications': '通知',
  'settings.privacy': '隐私',
  'settings.accessibility': '无障碍功能',
  'settings.appearance': '外观',
  'settings.language': '语言',
  'settings.theme': '主题',

  // Accessibility
  'a11y.darkMode': '深色模式',
  'a11y.fontSize': '字体大小',
  'a11y.highContrast': '高对比度',
  'a11y.screenReader': '屏幕阅读器支持',

  // Errors
  'error.notFound': '未找到页面',
  'error.unauthorized': '您无权访问此内容',
  'error.forbidden': '禁止访问',
  'error.serverError': '发生服务器错误',
  'error.timeout': '请求超时',
  'error.invalidEmail': '电子邮箱无效',
  'error.passwordTooShort': '密码至少需要 8 个字符',
  'error.passwordMismatch': '两次输入的密码不一致',

  // Onboarding
  'onboarding.welcome': '欢迎使用 NS Gaming Studio',
  'onboarding.step1': '入门指南',
  'onboarding.step2': '你的第一个游戏',
  'onboarding.step3': '分享你的游戏',
  'onboarding.complete': '设置完成',
  'onboarding.skipTutorial': '跳过教程',

  // AI Assistant
  'ai.gameBuddy': '游戏伙伴',
  'ai.helpWithCode': '代码帮助',
  'ai.suggestFeature': '功能建议',
  'ai.findBug': '查找漏洞',
  'ai.explain': '解释一下',
  'ai.improveCode': '优化代码',

  // Social
  'social.share': '分享',
  'social.like': '点赞',
  'social.comment': '评论',
  'social.follow': '关注',
  'social.unfollow': '取消关注',
  'social.friends': '好友',
  'social.followers': '粉丝',

  // Notifications
  'notif.newMessage': '新消息',
  'notif.friendRequest': '好友请求',
  'notif.classroomUpdate': '课堂更新',
  'notif.assignmentDue': '作业到期',
  'notif.gradeReceived': '收到成绩',
  'notif.achievementUnlocked': '解锁成就',

  // Time
  'time.today': '今天',
  'time.yesterday': '昨天',
  'time.thisWeek': '本周',
  'time.thisMonth': '本月',
  'time.justNow': '刚刚',

  // Lessons
  'lesson.createLesson': '创建课程',
  'lesson.editLesson': '编辑课程',
  'lesson.deleteLesson': '删除课程',
  'lesson.objectives': '学习目标',
  'lesson.materials': '教学材料',
  'lesson.activities': '活动',
  'lesson.assessment': '评估',

  // Templates
  'template.selectTemplate': '选择模板',
  'template.blankProject': '空白项目',
  'template.platformer': '平台跳跃游戏',
  'template.puzzle': '益智游戏',
  'template.runnerGame': '跑酷游戏',
  'template.shooterGame': '射击游戏',

  // Achievements
  'achievement.firstGame': '第一个游戏',
  'achievement.tenGames': '游戏创作者',
  'achievement.hundredLines': '代码大师',
  'achievement.shareGame': '分享达人',
  'achievement.helper': '热心助人',
};

export const VIETNAMESE_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': 'Trang chủ',
  'nav.dashboard': 'Bảng điều khiển',
  'nav.games': 'Trò chơi của tôi',
  'nav.projects': 'Dự án',
  'nav.classroom': 'Lớp học',
  'nav.help': 'Trợ giúp',
  'nav.settings': 'Cài đặt',
  'nav.profile': 'Hồ sơ',
  'nav.logout': 'Đăng xuất',
  'nav.signIn': 'Đăng nhập',
  'nav.signUp': 'Đăng ký',

  // Buttons
  'btn.newGame': 'Trò chơi mới',
  'btn.createProject': 'Tạo dự án',
  'btn.save': 'Lưu',
  'btn.cancel': 'Hủy',
  'btn.delete': 'Xóa',
  'btn.edit': 'Chỉnh sửa',
  'btn.share': 'Chia sẻ',
  'btn.play': 'Chơi',
  'btn.test': 'Kiểm tra',
  'btn.submit': 'Gửi',
  'btn.next': 'Tiếp theo',
  'btn.previous': 'Trước đó',
  'btn.finish': 'Hoàn thành',
  'btn.learn': 'Tìm hiểu thêm',

  // Labels
  'label.title': 'Tiêu đề',
  'label.description': 'Mô tả',
  'label.author': 'Tác giả',
  'label.date': 'Ngày',
  'label.status': 'Trạng thái',
  'label.tags': 'Thẻ',
  'label.difficulty': 'Độ khó',
  'label.language': 'Ngôn ngữ',
  'label.theme': 'Giao diện',

  // Forms
  'form.email': 'Địa chỉ email',
  'form.password': 'Mật khẩu',
  'form.confirmPassword': 'Xác nhận mật khẩu',
  'form.username': 'Tên người dùng',
  'form.firstName': 'Tên',
  'form.lastName': 'Họ',
  'form.schoolName': 'Tên trường',
  'form.gradeLevel': 'Khối lớp',
  'form.terms': 'Tôi đồng ý với Điều khoản dịch vụ',
  'form.privacy': 'Tôi chấp nhận Chính sách bảo mật',

  // Messages
  'msg.welcome': 'Chào mừng',
  'msg.loading': 'Đang tải...',
  'msg.error': 'Lỗi',
  'msg.success': 'Thành công!',
  'msg.saved': 'Đã lưu thành công',
  'msg.deleted': 'Đã xóa thành công',
  'msg.noResults': 'Không tìm thấy kết quả',
  'msg.tryAgain': 'Vui lòng thử lại',
  'msg.confirmDelete': 'Bạn có chắc chắn muốn xóa mục này không?',

  // Dashboard
  'dashboard.title': 'Bảng điều khiển của tôi',
  'dashboard.recentGames': 'Trò chơi gần đây',
  'dashboard.projects': 'Dự án',
  'dashboard.achievements': 'Thành tích',
  'dashboard.progress': 'Tiến độ',
  'dashboard.gamesCreated': 'Trò chơi đã tạo',
  'dashboard.hoursSpent': 'Số giờ đã dùng',
  'dashboard.friendsAdded': 'Bạn bè đã thêm',

  // Games
  'game.newGame': 'Trò chơi mới',
  'game.editGame': 'Chỉnh sửa trò chơi',
  'game.deleteGame': 'Xóa trò chơi',
  'game.shareGame': 'Chia sẻ trò chơi',
  'game.playGame': 'Chơi trò chơi',
  'game.codeEditor': 'Trình soạn thảo mã',
  'game.preview': 'Xem trước',
  'game.assets': 'Tài nguyên',
  'game.sounds': 'Âm thanh',
  'game.sprites': 'Hình ảnh động',

  // Code Editor
  'editor.undo': 'Hoàn tác',
  'editor.redo': 'Làm lại',
  'editor.copy': 'Sao chép',
  'editor.paste': 'Dán',
  'editor.delete': 'Xóa',
  'editor.format': 'Định dạng mã',
  'editor.console': 'Bảng điều khiển',
  'editor.errors': 'Lỗi',
  'editor.warnings': 'Cảnh báo',

  // Classroom
  'classroom.createClass': 'Tạo lớp học',
  'classroom.students': 'Học sinh',
  'classroom.assignments': 'Bài tập',
  'classroom.grades': 'Điểm số',
  'classroom.roster': 'Danh sách lớp',
  'classroom.settings': 'Cài đặt lớp học',
  'classroom.addStudent': 'Thêm học sinh',
  'classroom.removeStudent': 'Xóa học sinh',

  // Help
  'help.searchHelp': 'Tìm kiếm bài viết trợ giúp',
  'help.gettingStarted': 'Bắt đầu',
  'help.codeHelp': 'Trợ giúp về mã',
  'help.gameBuilding': 'Xây dựng trò chơi',
  'help.account': 'Tài khoản',
  'help.troubleshooting': 'Khắc phục sự cố',
  'help.faq': 'Câu hỏi thường gặp',
  'help.contact': 'Liên hệ hỗ trợ',

  // Settings
  'settings.profile': 'Hồ sơ',
  'settings.account': 'Tài khoản',
  'settings.notifications': 'Thông báo',
  'settings.privacy': 'Quyền riêng tư',
  'settings.accessibility': 'Trợ năng',
  'settings.appearance': 'Giao diện',
  'settings.language': 'Ngôn ngữ',
  'settings.theme': 'Giao diện',

  // Accessibility
  'a11y.darkMode': 'Chế độ tối',
  'a11y.fontSize': 'Cỡ chữ',
  'a11y.highContrast': 'Độ tương phản cao',
  'a11y.screenReader': 'Hỗ trợ trình đọc màn hình',

  // Errors
  'error.notFound': 'Không tìm thấy trang',
  'error.unauthorized': 'Bạn không có quyền truy cập nội dung này',
  'error.forbidden': 'Truy cập bị cấm',
  'error.serverError': 'Đã xảy ra lỗi máy chủ',
  'error.timeout': 'Yêu cầu đã hết thời gian chờ',
  'error.invalidEmail': 'Địa chỉ email không hợp lệ',
  'error.passwordTooShort': 'Mật khẩu phải có ít nhất 8 ký tự',
  'error.passwordMismatch': 'Mật khẩu không khớp',

  // Onboarding
  'onboarding.welcome': 'Chào mừng đến với NS Gaming Studio',
  'onboarding.step1': 'Bắt đầu',
  'onboarding.step2': 'Trò chơi đầu tiên của bạn',
  'onboarding.step3': 'Chia sẻ trò chơi của bạn',
  'onboarding.complete': 'Hoàn tất thiết lập',
  'onboarding.skipTutorial': 'Bỏ qua hướng dẫn',

  // AI Assistant
  'ai.gameBuddy': 'Bạn đồng hành trò chơi',
  'ai.helpWithCode': 'Trợ giúp về mã',
  'ai.suggestFeature': 'Đề xuất tính năng',
  'ai.findBug': 'Tìm lỗi',
  'ai.explain': 'Giải thích điều này',
  'ai.improveCode': 'Cải thiện mã',

  // Social
  'social.share': 'Chia sẻ',
  'social.like': 'Thích',
  'social.comment': 'Bình luận',
  'social.follow': 'Theo dõi',
  'social.unfollow': 'Bỏ theo dõi',
  'social.friends': 'Bạn bè',
  'social.followers': 'Người theo dõi',

  // Notifications
  'notif.newMessage': 'Tin nhắn mới',
  'notif.friendRequest': 'Lời mời kết bạn',
  'notif.classroomUpdate': 'Cập nhật lớp học',
  'notif.assignmentDue': 'Bài tập đến hạn',
  'notif.gradeReceived': 'Đã nhận điểm',
  'notif.achievementUnlocked': 'Mở khóa thành tích',

  // Time
  'time.today': 'Hôm nay',
  'time.yesterday': 'Hôm qua',
  'time.thisWeek': 'Tuần này',
  'time.thisMonth': 'Tháng này',
  'time.justNow': 'Vừa xong',

  // Lessons
  'lesson.createLesson': 'Tạo bài học',
  'lesson.editLesson': 'Chỉnh sửa bài học',
  'lesson.deleteLesson': 'Xóa bài học',
  'lesson.objectives': 'Mục tiêu học tập',
  'lesson.materials': 'Tài liệu',
  'lesson.activities': 'Hoạt động',
  'lesson.assessment': 'Đánh giá',

  // Templates
  'template.selectTemplate': 'Chọn một mẫu',
  'template.blankProject': 'Dự án trống',
  'template.platformer': 'Trò chơi platformer',
  'template.puzzle': 'Trò chơi giải đố',
  'template.runnerGame': 'Trò chơi chạy',
  'template.shooterGame': 'Trò chơi bắn súng',

  // Achievements
  'achievement.firstGame': 'Trò chơi đầu tiên',
  'achievement.tenGames': 'Người tạo trò chơi',
  'achievement.hundredLines': 'Bậc thầy lập trình',
  'achievement.shareGame': 'Bậc thầy chia sẻ',
  'achievement.helper': 'Người trợ giúp',
};

export const ARABIC_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': 'الرئيسية',
  'nav.dashboard': 'لوحة التحكم',
  'nav.games': 'ألعابي',
  'nav.projects': 'المشاريع',
  'nav.classroom': 'الفصل الدراسي',
  'nav.help': 'المساعدة',
  'nav.settings': 'الإعدادات',
  'nav.profile': 'الملف الشخصي',
  'nav.logout': 'تسجيل الخروج',
  'nav.signIn': 'تسجيل الدخول',
  'nav.signUp': 'إنشاء حساب',

  // Buttons
  'btn.newGame': 'لعبة جديدة',
  'btn.createProject': 'إنشاء مشروع',
  'btn.save': 'حفظ',
  'btn.cancel': 'إلغاء',
  'btn.delete': 'حذف',
  'btn.edit': 'تعديل',
  'btn.share': 'مشاركة',
  'btn.play': 'تشغيل',
  'btn.test': 'اختبار',
  'btn.submit': 'إرسال',
  'btn.next': 'التالي',
  'btn.previous': 'السابق',
  'btn.finish': 'إنهاء',
  'btn.learn': 'اعرف المزيد',

  // Labels
  'label.title': 'العنوان',
  'label.description': 'الوصف',
  'label.author': 'المؤلف',
  'label.date': 'التاريخ',
  'label.status': 'الحالة',
  'label.tags': 'الوسوم',
  'label.difficulty': 'مستوى الصعوبة',
  'label.language': 'اللغة',
  'label.theme': 'المظهر',

  // Forms
  'form.email': 'البريد الإلكتروني',
  'form.password': 'كلمة المرور',
  'form.confirmPassword': 'تأكيد كلمة المرور',
  'form.username': 'اسم المستخدم',
  'form.firstName': 'الاسم الأول',
  'form.lastName': 'اسم العائلة',
  'form.schoolName': 'اسم المدرسة',
  'form.gradeLevel': 'الصف الدراسي',
  'form.terms': 'أوافق على شروط الخدمة',
  'form.privacy': 'أقبل سياسة الخصوصية',

  // Messages
  'msg.welcome': 'مرحباً',
  'msg.loading': 'جارٍ التحميل...',
  'msg.error': 'خطأ',
  'msg.success': 'تم بنجاح!',
  'msg.saved': 'تم الحفظ بنجاح',
  'msg.deleted': 'تم الحذف بنجاح',
  'msg.noResults': 'لم يتم العثور على نتائج',
  'msg.tryAgain': 'يرجى المحاولة مرة أخرى',
  'msg.confirmDelete': 'هل أنت متأكد من أنك تريد حذف هذا؟',

  // Dashboard
  'dashboard.title': 'لوحة التحكم الخاصة بي',
  'dashboard.recentGames': 'الألعاب الأخيرة',
  'dashboard.projects': 'المشاريع',
  'dashboard.achievements': 'الإنجازات',
  'dashboard.progress': 'التقدم',
  'dashboard.gamesCreated': 'الألعاب المنشأة',
  'dashboard.hoursSpent': 'الساعات المستغرقة',
  'dashboard.friendsAdded': 'الأصدقاء المضافون',

  // Games
  'game.newGame': 'لعبة جديدة',
  'game.editGame': 'تعديل اللعبة',
  'game.deleteGame': 'حذف اللعبة',
  'game.shareGame': 'مشاركة اللعبة',
  'game.playGame': 'تشغيل اللعبة',
  'game.codeEditor': 'محرر الأكواد',
  'game.preview': 'معاينة',
  'game.assets': 'الموارد',
  'game.sounds': 'الأصوات',
  'game.sprites': 'الرسوم المتحركة',

  // Code Editor
  'editor.undo': 'تراجع',
  'editor.redo': 'إعادة',
  'editor.copy': 'نسخ',
  'editor.paste': 'لصق',
  'editor.delete': 'حذف',
  'editor.format': 'تنسيق الكود',
  'editor.console': 'وحدة التحكم',
  'editor.errors': 'الأخطاء',
  'editor.warnings': 'التحذيرات',

  // Classroom
  'classroom.createClass': 'إنشاء فصل دراسي',
  'classroom.students': 'الطلاب',
  'classroom.assignments': 'الواجبات',
  'classroom.grades': 'الدرجات',
  'classroom.roster': 'قائمة الفصل',
  'classroom.settings': 'إعدادات الفصل',
  'classroom.addStudent': 'إضافة طالب',
  'classroom.removeStudent': 'إزالة طالب',

  // Help
  'help.searchHelp': 'البحث في مقالات المساعدة',
  'help.gettingStarted': 'البدء',
  'help.codeHelp': 'مساعدة في الأكواد',
  'help.gameBuilding': 'بناء الألعاب',
  'help.account': 'الحساب',
  'help.troubleshooting': 'استكشاف الأخطاء وإصلاحها',
  'help.faq': 'الأسئلة الشائعة',
  'help.contact': 'الاتصال بالدعم',

  // Settings
  'settings.profile': 'الملف الشخصي',
  'settings.account': 'الحساب',
  'settings.notifications': 'الإشعارات',
  'settings.privacy': 'الخصوصية',
  'settings.accessibility': 'إمكانية الوصول',
  'settings.appearance': 'المظهر',
  'settings.language': 'اللغة',
  'settings.theme': 'المظهر',

  // Accessibility
  'a11y.darkMode': 'الوضع الداكن',
  'a11y.fontSize': 'حجم الخط',
  'a11y.highContrast': 'تباين عالٍ',
  'a11y.screenReader': 'دعم قارئ الشاشة',

  // Errors
  'error.notFound': 'الصفحة غير موجودة',
  'error.unauthorized': 'ليس لديك إذن للوصول إلى هذا',
  'error.forbidden': 'الوصول محظور',
  'error.serverError': 'حدث خطأ في الخادم',
  'error.timeout': 'انتهت مهلة الطلب',
  'error.invalidEmail': 'عنوان بريد إلكتروني غير صالح',
  'error.passwordTooShort': 'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل',
  'error.passwordMismatch': 'كلمتا المرور غير متطابقتين',

  // Onboarding
  'onboarding.welcome': 'مرحباً بك في NS Gaming Studio',
  'onboarding.step1': 'البدء',
  'onboarding.step2': 'لعبتك الأولى',
  'onboarding.step3': 'شارك لعبتك',
  'onboarding.complete': 'اكتمل الإعداد',
  'onboarding.skipTutorial': 'تخطّي الشرح التعليمي',

  // AI Assistant
  'ai.gameBuddy': 'رفيق الألعاب',
  'ai.helpWithCode': 'المساعدة في الأكواد',
  'ai.suggestFeature': 'اقتراح ميزة',
  'ai.findBug': 'العثور على خطأ',
  'ai.explain': 'اشرح هذا',
  'ai.improveCode': 'تحسين الكود',

  // Social
  'social.share': 'مشاركة',
  'social.like': 'إعجاب',
  'social.comment': 'تعليق',
  'social.follow': 'متابعة',
  'social.unfollow': 'إلغاء المتابعة',
  'social.friends': 'الأصدقاء',
  'social.followers': 'المتابعون',

  // Notifications
  'notif.newMessage': 'رسالة جديدة',
  'notif.friendRequest': 'طلب صداقة',
  'notif.classroomUpdate': 'تحديث الفصل',
  'notif.assignmentDue': 'موعد تسليم الواجب',
  'notif.gradeReceived': 'تم استلام الدرجة',
  'notif.achievementUnlocked': 'تم فتح إنجاز',

  // Time
  'time.today': 'اليوم',
  'time.yesterday': 'أمس',
  'time.thisWeek': 'هذا الأسبوع',
  'time.thisMonth': 'هذا الشهر',
  'time.justNow': 'الآن',

  // Lessons
  'lesson.createLesson': 'إنشاء درس',
  'lesson.editLesson': 'تعديل الدرس',
  'lesson.deleteLesson': 'حذف الدرس',
  'lesson.objectives': 'أهداف التعلم',
  'lesson.materials': 'المواد التعليمية',
  'lesson.activities': 'الأنشطة',
  'lesson.assessment': 'التقييم',

  // Templates
  'template.selectTemplate': 'اختر قالباً',
  'template.blankProject': 'مشروع فارغ',
  'template.platformer': 'لعبة منصات',
  'template.puzzle': 'لعبة ألغاز',
  'template.runnerGame': 'لعبة جري',
  'template.shooterGame': 'لعبة إطلاق نار',

  // Achievements
  'achievement.firstGame': 'اللعبة الأولى',
  'achievement.tenGames': 'صانع الألعاب',
  'achievement.hundredLines': 'خبير البرمجة',
  'achievement.shareGame': 'خبير المشاركة',
  'achievement.helper': 'المساعد',
};

export const TAGALOG_TRANSLATIONS: TranslationStrings = {
  // Navigation
  'nav.home': 'Tahanan',
  'nav.dashboard': 'Dashboard',
  'nav.games': 'Aking mga Laro',
  'nav.projects': 'Mga Proyekto',
  'nav.classroom': 'Silid-aralan',
  'nav.help': 'Tulong',
  'nav.settings': 'Mga Setting',
  'nav.profile': 'Profile',
  'nav.logout': 'Mag-log out',
  'nav.signIn': 'Mag-sign in',
  'nav.signUp': 'Mag-sign up',

  // Buttons
  'btn.newGame': 'Bagong Laro',
  'btn.createProject': 'Gumawa ng Proyekto',
  'btn.save': 'I-save',
  'btn.cancel': 'Kanselahin',
  'btn.delete': 'Burahin',
  'btn.edit': 'I-edit',
  'btn.share': 'Ibahagi',
  'btn.play': 'Maglaro',
  'btn.test': 'Subukan',
  'btn.submit': 'Isumite',
  'btn.next': 'Susunod',
  'btn.previous': 'Nakaraan',
  'btn.finish': 'Tapusin',
  'btn.learn': 'Matuto Pa',

  // Labels
  'label.title': 'Pamagat',
  'label.description': 'Paglalarawan',
  'label.author': 'May-akda',
  'label.date': 'Petsa',
  'label.status': 'Katayuan',
  'label.tags': 'Mga Tag',
  'label.difficulty': 'Hirap',
  'label.language': 'Wika',
  'label.theme': 'Tema',

  // Forms
  'form.email': 'Email Address',
  'form.password': 'Password',
  'form.confirmPassword': 'Kumpirmahin ang Password',
  'form.username': 'Username',
  'form.firstName': 'Pangalan',
  'form.lastName': 'Apelyido',
  'form.schoolName': 'Pangalan ng Paaralan',
  'form.gradeLevel': 'Antas ng Grado',
  'form.terms': 'Sumasang-ayon ako sa Mga Tuntunin ng Serbisyo',
  'form.privacy': 'Tinatanggap ko ang Patakaran sa Privacy',

  // Messages
  'msg.welcome': 'Maligayang pagdating',
  'msg.loading': 'Naglo-load...',
  'msg.error': 'Error',
  'msg.success': 'Tagumpay!',
  'msg.saved': 'Matagumpay na na-save',
  'msg.deleted': 'Matagumpay na nabura',
  'msg.noResults': 'Walang nahanap na resulta',
  'msg.tryAgain': 'Pakisubukang muli',
  'msg.confirmDelete': 'Sigurado ka bang gusto mong burahin ito?',

  // Dashboard
  'dashboard.title': 'Aking Dashboard',
  'dashboard.recentGames': 'Mga Kamakailang Laro',
  'dashboard.projects': 'Mga Proyekto',
  'dashboard.achievements': 'Mga Tagumpay',
  'dashboard.progress': 'Pag-unlad',
  'dashboard.gamesCreated': 'Mga Larong Nagawa',
  'dashboard.hoursSpent': 'Mga Oras na Ginugol',
  'dashboard.friendsAdded': 'Mga Kaibigang Naidagdag',

  // Games
  'game.newGame': 'Bagong Laro',
  'game.editGame': 'I-edit ang Laro',
  'game.deleteGame': 'Burahin ang Laro',
  'game.shareGame': 'Ibahagi ang Laro',
  'game.playGame': 'Maglaro',
  'game.codeEditor': 'Code Editor',
  'game.preview': 'Preview',
  'game.assets': 'Mga Asset',
  'game.sounds': 'Mga Tunog',
  'game.sprites': 'Mga Sprite',

  // Code Editor
  'editor.undo': 'I-undo',
  'editor.redo': 'I-redo',
  'editor.copy': 'Kopyahin',
  'editor.paste': 'I-paste',
  'editor.delete': 'Burahin',
  'editor.format': 'I-format ang Code',
  'editor.console': 'Console',
  'editor.errors': 'Mga Error',
  'editor.warnings': 'Mga Babala',

  // Classroom
  'classroom.createClass': 'Gumawa ng Silid-aralan',
  'classroom.students': 'Mga Mag-aaral',
  'classroom.assignments': 'Mga Takdang-aralin',
  'classroom.grades': 'Mga Grado',
  'classroom.roster': 'Listahan ng Klase',
  'classroom.settings': 'Mga Setting ng Silid-aralan',
  'classroom.addStudent': 'Magdagdag ng Mag-aaral',
  'classroom.removeStudent': 'Alisin ang Mag-aaral',

  // Help
  'help.searchHelp': 'Maghanap ng mga artikulo ng tulong',
  'help.gettingStarted': 'Pagsisimula',
  'help.codeHelp': 'Tulong sa Code',
  'help.gameBuilding': 'Paggawa ng Laro',
  'help.account': 'Account',
  'help.troubleshooting': 'Pag-troubleshoot',
  'help.faq': 'Mga Madalas na Tanong',
  'help.contact': 'Makipag-ugnayan sa Suporta',

  // Settings
  'settings.profile': 'Profile',
  'settings.account': 'Account',
  'settings.notifications': 'Mga Abiso',
  'settings.privacy': 'Privacy',
  'settings.accessibility': 'Accessibility',
  'settings.appearance': 'Hitsura',
  'settings.language': 'Wika',
  'settings.theme': 'Tema',

  // Accessibility
  'a11y.darkMode': 'Dark Mode',
  'a11y.fontSize': 'Laki ng Font',
  'a11y.highContrast': 'Mataas na Contrast',
  'a11y.screenReader': 'Suporta sa Screen Reader',

  // Errors
  'error.notFound': 'Hindi nahanap ang pahina',
  'error.unauthorized': 'Wala kang awtorisasyon na ma-access ito',
  'error.forbidden': 'Bawal ang pag-access',
  'error.serverError': 'Nagkaroon ng error sa server',
  'error.timeout': 'Nag-time out ang kahilingan',
  'error.invalidEmail': 'Hindi wastong email address',
  'error.passwordTooShort': 'Dapat hindi bababa sa 8 character ang password',
  'error.passwordMismatch': 'Hindi magkatugma ang mga password',

  // Onboarding
  'onboarding.welcome': 'Maligayang pagdating sa NS Gaming Studio',
  'onboarding.step1': 'Pagsisimula',
  'onboarding.step2': 'Ang Iyong Unang Laro',
  'onboarding.step3': 'Ibahagi ang Iyong Laro',
  'onboarding.complete': 'Tapos na ang Setup',
  'onboarding.skipTutorial': 'Laktawan ang Tutorial',

  // AI Assistant
  'ai.gameBuddy': 'Game Buddy',
  'ai.helpWithCode': 'Tulong sa Code',
  'ai.suggestFeature': 'Magmungkahi ng Feature',
  'ai.findBug': 'Maghanap ng Bug',
  'ai.explain': 'Ipaliwanag Ito',
  'ai.improveCode': 'Pagbutihin ang Code',

  // Social
  'social.share': 'Ibahagi',
  'social.like': 'I-like',
  'social.comment': 'Magkomento',
  'social.follow': 'I-follow',
  'social.unfollow': 'I-unfollow',
  'social.friends': 'Mga Kaibigan',
  'social.followers': 'Mga Tagasubaybay',

  // Notifications
  'notif.newMessage': 'Bagong Mensahe',
  'notif.friendRequest': 'Friend Request',
  'notif.classroomUpdate': 'Update sa Silid-aralan',
  'notif.assignmentDue': 'Takdang-aralin na Dapat Ipasa',
  'notif.gradeReceived': 'Natanggap na Grado',
  'notif.achievementUnlocked': 'Na-unlock na Tagumpay',

  // Time
  'time.today': 'Ngayon',
  'time.yesterday': 'Kahapon',
  'time.thisWeek': 'Ngayong Linggo',
  'time.thisMonth': 'Ngayong Buwan',
  'time.justNow': 'Kaka-lang',

  // Lessons
  'lesson.createLesson': 'Gumawa ng Aralin',
  'lesson.editLesson': 'I-edit ang Aralin',
  'lesson.deleteLesson': 'Burahin ang Aralin',
  'lesson.objectives': 'Mga Layunin sa Pagkatuto',
  'lesson.materials': 'Mga Materyales',
  'lesson.activities': 'Mga Aktibidad',
  'lesson.assessment': 'Pagtatasa',

  // Templates
  'template.selectTemplate': 'Pumili ng Template',
  'template.blankProject': 'Blangkong Proyekto',
  'template.platformer': 'Platformer na Laro',
  'template.puzzle': 'Puzzle na Laro',
  'template.runnerGame': 'Runner na Laro',
  'template.shooterGame': 'Shooter na Laro',

  // Achievements
  'achievement.firstGame': 'Unang Laro',
  'achievement.tenGames': 'Tagalikha ng Laro',
  'achievement.hundredLines': 'Dalubhasa sa Code',
  'achievement.shareGame': 'Dalubhasa sa Pagbabahagi',
  'achievement.helper': 'Katulong',
};

// P4 fix (2026-05-17): added English fallback when a key is missing from the
// active locale's table. Previously a missing Spanish key would render the raw
// key (e.g. 'btn.share' literally). i18next's documented "safety net" pattern.
export const getTranslation = (
  key: string,
  language: string = DEFAULT_LANGUAGE
): string => {
  let translations = ENGLISH_TRANSLATIONS;

  if (language === 'es') {
    translations = SPANISH_TRANSLATIONS;
  } else if (language === 'zh') {
    translations = CHINESE_TRANSLATIONS;
  } else if (language === 'vi') {
    translations = VIETNAMESE_TRANSLATIONS;
  } else if (language === 'ar') {
    translations = ARABIC_TRANSLATIONS;
  } else if (language === 'tl') {
    translations = TAGALOG_TRANSLATIONS;
  }

  // Fall back to English if the active locale doesn't have this key.
  const translated = translations[key];
  if (translated !== undefined) return translated;

  const englishFallback = ENGLISH_TRANSLATIONS[key];
  if (englishFallback !== undefined) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing translation for "${key}" in "${language}" — using English fallback`);
    }
    return englishFallback;
  }

  // Key not in English either — surface the raw key so QA notices.
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Unknown translation key "${key}"`);
  }
  return key;
};

export const getCurrentLanguage = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const stored = localStorage.getItem('app-language');
  if (stored && SUPPORTED_LANGUAGES.find((l) => l.code === stored)) {
    return stored;
  }

  return DEFAULT_LANGUAGE;
};

export const setLanguage = (code: string): boolean => {
  if (!SUPPORTED_LANGUAGES.find((l) => l.code === code)) {
    return false;
  }

  if (typeof window !== 'undefined') {
    localStorage.setItem('app-language', code);
  }

  return true;
};
